import React, { useState, useEffect, useRef, useMemo } from 'react';
import { Button, Icon, Select, TextArea } from '@aidkitorg/component-library';
import { useLocalizedStrings } from '../Localization';
import { ConversationItemWithAKMeta } from '@aidkitorg/types/lib/convoai/conversation';
import { useAICoach } from './AICoachContext';

export const FeedbackButtons = React.memo((props: ConversationItemWithAKMeta["meta"]["feedback"] & { id: string }) => {
  const { chatChannel } = useAICoach();
  const L = useLocalizedStrings();
  const [additionalFeedbackInput, setAdditionalFeedbackInput] = useState<string>(props.additionalFeedback ?? "");
  const [showSuccess, setShowSuccess] = useState<boolean>(false);
  const { id, ...feedback } = props;
    
  const FeedbackReasons = [
    { value: '', i18nKey: "feedback_select_reason" },
    { value: 'too_slow', i18nKey: "feedback_too_slow" },
    { value: 'not_accurate', i18nKey: "feedback_not_accurate" },
    { value: 'tone', i18nKey: "feedback_tone" },
    { value: 'other', i18nKey: "feedback_other" }
  ].map(
    option => ({
      value: option.value,
      label: L.ai_coach[option.i18nKey as keyof typeof L.ai_coach]
    })
  );
    
  const selectedReason = useMemo(() => { 
    return FeedbackReasons.find(r => r.value === props.reason) ?? FeedbackReasons[0];
  }, [props.reason])
  
  if (!chatChannel) {
    return null;
  }
    
  return (
    <div className="flex flex-col gap-2">
      <div className="flex items-center gap-2 text-sm">
        <Button
          variant={props.rating === 'up' ? 'primary' : 'secondary'}
          shape="circle"
          size="sm"
          icon={<Icon name="HandThumbUpIcon" />}
          onClick={() => {
            chatChannel.dispatch({
              type: "update-item-meta",
              id,
              update: {
                feedback: {
                  ...feedback,
                  reason: null,
                  additionalFeedback: null,
                  rating: props.rating === "up" ? undefined : "up"
                }
              }
            });
          }}
        />
        <Button
          variant={props.rating === 'down' ? 'primary' : 'secondary'}
          shape="circle"
          size="sm"
          icon={<Icon name="HandThumbDownIcon" />}
          onClick={() => {
            chatChannel.dispatch({
              type: "update-item-meta",
              id,
              update: {
                feedback: {
                  ...feedback,
                  // reason: null,
                  // additionalFeedback: null,
                  rating: props.rating === "down" ? undefined : "down"
                }
              }
            });
          }}
        />
        <div className="text-xs">{L.ai_coach.feedback_was_this_helpful}</div>
      </div>
      
      {props.rating === 'down' && (
        <>
          <Select
            label={L.ai_coach.feedback_tell_us_more}
            labelLocation="above"
            description={L.ai_coach.feedback_select_reason}
            options={selectedReason ? FeedbackReasons.slice(1) : FeedbackReasons}
            value={selectedReason}
            onChange={(option) => {
              chatChannel.dispatch({
                type: "update-item-meta",
                id,
                update: {
                  feedback: {
                    ...feedback,
                    reason: option.value
                  }
                }
              });
            }}
          />
          {props.reason === 'other' && <form 
            className="flex flex-col gap-2"
            onSubmit={
              e => { 
                e.preventDefault();
                chatChannel.dispatch({
                  type: "update-item-meta",
                  id,
                  update: {
                    feedback: {
                      ...feedback,
                      additionalFeedback: additionalFeedbackInput
                    }
                  }
                });
                setShowSuccess(true)              
              }
            }>
            <TextArea
              rows={2}
              label={L.ai_coach.feedback_additional}
              value={additionalFeedbackInput}
              onChange={e => setAdditionalFeedbackInput(e.target.value)}
            />          
            <Button 
              size="sm"
              disabled={!additionalFeedbackInput}
              type="submit"
            >
              {L.applicant.meeting.submit}
            </Button>
          </form>}
          
          {(props.rating === "down" && props.reason && props.additionalFeedback) && <div className="text-green-600 text-sm mt-2">{L.ai_coach.feedback_thanks}</div>}
          
        </>
      )}
    </div>
  );
});
